import React from 'react';
import AddressAutocomplete from './AddressAutocomplete';
import LocationPicker from './LocationPicker';
import './AddressPicker.css';

/** Rayon de confidentialite affiche sur le site public, en metres.
 *  GLOBAL a toute l'application (decide le 2026-08-18). Miroir de
 *  RAYON_CONFIDENTIALITE_M dans coloc-front/src/components/coloc/
 *  MapWithCircle.tsx : deux applications distinctes (meme depot depuis le
 *  2026-09-25), sans module partage entre elles. */
const RAYON_CONFIDENTIALITE_M = 100;

export interface AdressePostale {
  address: string;
  zipCode: string;
  city: string;
  countryCode: string;
  /** [latitude, longitude] — null tant que la position n'est pas connue. */
  location: [number, number] | null;
}

interface Props {
  value: AdressePostale;
  onChange: (valeur: AdressePostale) => void;
  /** Le wizard rend un formulaire compact, la fiche Détails un formulaire
   *  large : seule la mise en page diffère, jamais la logique. */
  variante?: 'wizard' | 'fiche';
}

const PAYS = [
  ['FR', 'France'],
  ['BE', 'Belgique'],
  ['CH', 'Suisse'],
  ['LU', 'Luxembourg'],
];

/**
 * Bloc adresse complet : recherche assistée, champs structurés et carte de
 * réglage. Composant UNIQUE, partagé par le wizard et la fiche Détails —
 * dupliquer ce bloc garantirait qu'une correction ne soit appliquée qu'à un
 * seul des deux endroits.
 */
const AddressPicker: React.FC<Props> = ({ value, onChange, variante = 'fiche' }) => {
  const modifier = (partiel: Partial<AdressePostale>) =>
    onChange({ ...value, ...partiel });

  return (
    <div className={`adresse-bloc adresse-bloc--${variante}`}>
      <label className="adresse-bloc-champ">
        <span>Adresse *</span>
        <AddressAutocomplete
          value={value.address}
          required
          placeholder="Commencez à taper, puis choisissez dans la liste"
          // Retaper invalide la position : une position ne doit jamais
          // survivre au texte qui l'a produite.
          onChange={(v) => modifier({ address: v, location: null })}
          onResolved={(d) =>
            modifier({
              address: d.address,
              zipCode: d.zipCode || value.zipCode,
              city: d.city || value.city,
              countryCode: d.countryCode || value.countryCode,
              location:
                d.latitude != null && d.longitude != null
                  ? [d.latitude, d.longitude]
                  : null,
            })
          }
        />
      </label>

      <div className="adresse-bloc-ligne">
        <label className="adresse-bloc-champ">
          <span>Code postal *</span>
          <input
            type="text"
            value={value.zipCode}
            required
            onChange={(e) => modifier({ zipCode: e.target.value })}
          />
        </label>
        <label className="adresse-bloc-champ">
          <span>Ville *</span>
          <input
            type="text"
            value={value.city}
            required
            onChange={(e) => modifier({ city: e.target.value })}
          />
        </label>
        <label className="adresse-bloc-champ">
          <span>Pays *</span>
          <select
            value={value.countryCode}
            required
            onChange={(e) => modifier({ countryCode: e.target.value })}
          >
            {PAYS.map(([code, nom]) => (
              <option key={code} value={code}>{nom}</option>
            ))}
          </select>
        </label>
      </div>

      <p className={`adresse-bloc-etat ${value.location ? 'ok' : 'ko'}`}>
        {value.location
          ? `✓ Position enregistrée (${value.location[0].toFixed(5)}, ${value.location[1].toFixed(5)})`
          : "Position non renseignée — la carte de votre site public en a besoin, et elle est exigée pour la mise en ligne."}
      </p>

      <LocationPicker
        value={value.location}
        onChange={(position) => modifier({ location: position })}
      />

      {/* Rassurer explicitement : un bailleur hésite à poser sa position au
          mètre près sur un site public, et il a raison de se poser la question.
          RAYON_CONFIDENTIALITE_M vit dans coloc-front (MapWithCircle) — dépôts
          distincts, donc valeur dupliquée : les deux doivent bouger ensemble. */}
      <p className="adresse-bloc-confidentialite">
        <strong>Cette position n&apos;est jamais affichée telle quelle.</strong>{' '}
        Le site public montre un cercle de {RAYON_CONFIDENTIALITE_M} mètres autour
        d&apos;elle, sans repère au centre : les candidats situent le quartier, pas
        la porte. Elle sert aussi à calculer les distances aux points d&apos;intérêt.
      </p>
    </div>
  );
};

export default AddressPicker;
